import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import ReactPaginate from "react-paginate";
import { useParams } from "react-router-dom";
import { Dropdown, Spinner } from "react-bootstrap";

import {
  exportReportApi,
  getReportByCodeApi,
  ReportExportFormat,
  runReportApi,
} from "../api/reportApi";
import {
  FilterValues,
  ReportByCodeResponse,
  ReportColumnProps,
  RunReportResponse,
  RunReportPayload,
  SortConfig,
} from "../types/report";
import DataTable from "src/components/table/DataTable";
import FilterBar from "src/components/filters";
import { downloadExportFile, ReportExportResponse } from "src/helpers/Report";
import { getInitialValues } from "src/helpers/Filter";
import { END_DATE, START_DATE } from "src/constants/filter";

const DEFAULT_MONTH_FILTERS: Record<string, string | number> = {
  start_date: START_DATE,
  end_date: END_DATE,
};

const SKELETON_COLUMNS: ReportColumnProps[] = [
  {
    key: "skeleton_col_1",
    label: "Column 1",
    description: null,
    data_type: "string",
    sortable: false,
    filterable: false,
    format: null,
    width: null,
    is_masked: false,
    mask_pattern: null,
  },
  {
    key: "skeleton_col_2",
    label: "Column 2",
    description: null,
    data_type: "string",
    sortable: false,
    filterable: false,
    format: null,
    width: null,
    is_masked: false,
    mask_pattern: null,
  },
  {
    key: "skeleton_col_3",
    label: "Column 3",
    description: null,
    data_type: "string",
    sortable: false,
    filterable: false,
    format: null,
    width: null,
    is_masked: false,
    mask_pattern: null,
  },
  {
    key: "skeleton_col_4",
    label: "Column 4",
    description: null,
    data_type: "string",
    sortable: false,
    filterable: false,
    format: null,
    width: null,
    is_masked: false,
    mask_pattern: null,
  },
];

const extractRows = (
  response: RunReportResponse | undefined,
): Array<Record<string, unknown>> => {
  if (!response?.rows) return [];
  if (Array.isArray(response.rows)) return response.rows;
  return [];
};

const extractMeta = (
  response: RunReportResponse | undefined,
): { total: number; page: number; page_size: number } => ({
  total: response?.meta?.total ?? 0,
  page: response?.meta?.page ?? 1,
  page_size: response?.meta?.page_size ?? 20,
});

export default function ReportPage() {
  const { report_name } = useParams<{ report_name: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  // FilterValues because FilterBar is shared with the dashboard, which has a
  // multi-select. This page passes no `multiSelectKeys`, so every value here is
  // a scalar — the reports backend matches a product with `=`.
  const [appliedFilters, setAppliedFilters] =
    useState<FilterValues>(DEFAULT_MONTH_FILTERS);
  const pageSize = 50;

  const { data: report, isLoading: isReportLoading } = useQuery<
    ReportByCodeResponse,
    Error
  >({
    queryKey: ["reports", report_name],
    queryFn: () => getReportByCodeApi(report_name as string),
    enabled: !!report_name,
  });

  const filtersJson = report?.filters ?? [];
  const initialFilters =
    filtersJson.length > 0
      ? getInitialValues(filtersJson)
      : DEFAULT_MONTH_FILTERS;

  const payload: RunReportPayload = {
    page: {
      number: currentPage,
      size: pageSize,
    },
    filters: appliedFilters,
    ...(sortConfig && { sort: sortConfig }),
  };

  const { data: runResponse, isLoading: isRowsLoading } = useQuery<
    RunReportResponse,
    Error
  >({
    queryKey: [
      "reports",
      report_name,
      "run",
      currentPage,
      pageSize,
      sortConfig,
      appliedFilters,
    ],
    queryFn: () =>
      runReportApi<RunReportResponse>(report_name as string, payload),
    enabled: !!report_name,
    // Never serve stale data — always hit the backend on every page/sort change
    staleTime: 0,
    // Remove from cache immediately so navigating back always refetches
    gcTime: 0,
  });

  const { mutate: downloadReport, isPending: isDownloadPending } = useMutation<
    ReportExportResponse,
    Error,
    ReportExportFormat
  >({
    mutationFn: (format) =>
      exportReportApi(report_name ?? "", { filters: appliedFilters }, format),
    onSuccess: (response) => {
      // Same provenance block as the widget downloads (CSV only; xlsx exports
      // pass through untouched).
      downloadExportFile(response, { appliedFilters });
    },
  });

  const reportRows = extractRows(runResponse);
  const paginationMeta = extractMeta(runResponse);
  const pageCountFromTotal = Math.max(
    1,
    Math.ceil((paginationMeta.total ?? 0) / pageSize),
  );
  const isLikelyLastPage = !isRowsLoading && reportRows.length < pageSize;
  const pageCount = isLikelyLastPage
    ? Math.max(1, Math.min(pageCountFromTotal, currentPage))
    : Math.max(pageCountFromTotal, currentPage);
  const tableHeaders =
    report?.columns && report.columns.length > 0
      ? report.columns
      : SKELETON_COLUMNS;

  useEffect(() => {
    if (report_name) {
      setAppliedFilters(DEFAULT_MONTH_FILTERS);
      if (sortConfig) setSortConfig(null);
      if (currentPage !== 1) setCurrentPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report_name]);

  /**
   * Handles column header sort clicks.
   *
   * Rules:
   *  - Clicking a new column  → sort asc on that column, reset to page 1
   *  - Clicking the same col  → toggle asc ↔ desc, reset to page 1
   *
   * Page is always reset to 1 because sorted results are a different ordered
   * dataset — staying on page N of an unsorted set makes no sense after sorting.
   */
  const handleSort = (column: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.column !== column) {
        return { column, direction: "desc" };
      }
      return {
        column,
        direction: prev.direction === "asc" ? "desc" : "asc",
      };
    });
  };

  if (!report_name) {
    return <p>Missing report code in URL.</p>;
  }

  return (
    <section className="reports-page d-flex flex-fill overflow-auto">
      <div className="container-fluid px-3 px-lg-5 pt-2 d-flex flex-column flex-fill">
        <div className="d-flex align-items-center justify-content-between">
          {isReportLoading ? (
              <div className="placeholder-glow mb-2 col-5">
                <span className="placeholder w-100" style={{height:48}} />
              </div>
              ): (
            <h1 className="auth-title text-secondary-light me-5">{report?.name}</h1>
          )}
          <div>
            <Dropdown align="end">
              <Dropdown.Toggle
                  className="btn btn-success"
                  disabled={isDownloadPending}
              >
                {isDownloadPending ? (
                    <Spinner
                        className="me-1"
                        animation="border"
                        size="sm"
                        role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                ) : (
                    <>
                      <i className="bi bi-download"></i>&nbsp;
                    </>
                )}
                Download
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item
                    as="button"
                    onClick={() => downloadReport("xlsx")}
                    disabled={isDownloadPending}
                >
                  XLSX
                </Dropdown.Item>
                <Dropdown.Item
                    as="button"
                    onClick={() => downloadReport("csv")}
                    disabled={isDownloadPending}
                >
                  CSV
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="my-3">
          <FilterBar
            inputFields={filtersJson}
            defaultValues={appliedFilters}
            showLabels={false}
            onClear={() => {
              setCurrentPage(1);
              setAppliedFilters(initialFilters);
            }}
            onSubmit={({ fields }) => {
              setCurrentPage(1);
              setAppliedFilters(fields);
            }}
          />
        </div>

        <div className="card mb-4 flex-fill overflow-auto" style={{ height: "600px" }}>
          <div
            className="card-body p-0 d-flex flex-column overflow-auto"
            style={{ flex: "1 1 0" }}
          >
            <DataTable
              isReportLoading={isRowsLoading}
              reportHeader={tableHeaders}
              reportData={reportRows}
              sortConfig={sortConfig}
              onSort={handleSort}
            />
            {pageCount > 1 && (
              <nav className="pagination-nav justify-content-between align-items-center flex-column flex-md-row">
                <p className="m-0">
                  <small className="text-muted">
                    {isRowsLoading
                        ? "Loading report data..."
                        : `Showing ${reportRows.length} row(s) • Total ${paginationMeta.total}`}
                  </small>
                </p>
                <ReactPaginate
                  breakLabel="..."
                  nextLabel={
                    <>
                      Next <i className="bi bi-chevron-right"></i>
                    </>
                  }
                  onPageChange={({ selected }: { selected: number }) =>
                    setCurrentPage(selected + 1)
                  }
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={2}
                  pageCount={pageCount}
                  previousLabel={
                    <>
                      <i className="bi bi-chevron-left"></i> Previous
                    </>
                  }
                  forcePage={currentPage - 1}
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                  containerClassName="pagination"
                  activeClassName="active"
                />
                <p className="m-0">
                  <small className="text-muted">Page {currentPage}</small>
                </p>
              </nav>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
